import { useState, useEffect } from 'react';
import { 
  Database, 
  Search,
  Filter,
  Download,
  Info,
  CheckCircle,
  Zap,
  Package,
  FileText,
  BookOpen,
  DollarSign,
  ExternalLink,
  Tag,
  Plus,
  Edit2,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import { 
  deviceDatabase, 
  getAllManufacturers, 
  getAllCategories,
  type DeviceSpec 
} from '../data/deviceDatabase';
import { 
  getEnhancedDeviceData, 
  getCompatiblePanels,
  getDiscountPercentage 
} from '../data/deviceEnhancedData';

const CUSTOM_DEVICES_KEY = 'customDevices';

export function DeviceDatabasePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDevice, setSelectedDevice] = useState<DeviceSpec | null>(null);

  const manufacturers = getAllManufacturers();
  const categories = getAllCategories();

  const filteredDevices = deviceDatabase.filter(device => {
    const matchesSearch = searchTerm === '' || 
      device.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesManufacturer = selectedManufacturer === 'all' || 
      device.manufacturer === selectedManufacturer;
    
    const matchesCategory = selectedCategory === 'all' || 
      device.category === selectedCategory;

    return matchesSearch && matchesManufacturer && matchesCategory;
  });

  const exportDatabase = () => {
    const dataStr = JSON.stringify(filteredDevices, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `device_database_${Date.now()}.json`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Database className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Device Database</h1>
            <p className="text-xl text-indigo-100">
              Comprehensive library of fire alarm devices with real manufacturer specifications
            </p>
            <div className="mt-6 flex justify-center gap-6 text-sm">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>{deviceDatabase.length} Devices</span>
              </div>
              <div className="flex items-center">
                <Package className="w-5 h-5 mr-2" />
                <span>{manufacturers.length} Manufacturers</span>
              </div>
              <div className="flex items-center">
                <Zap className="w-5 h-5 mr-2" />
                <span>UL Listed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Search className="w-4 h-4 inline mr-2" />
                Search Devices
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by model, description..."
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Filter className="w-4 h-4 inline mr-2" />
                Manufacturer
              </label>
              <select
                value={selectedManufacturer}
                onChange={(e) => setSelectedManufacturer(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Manufacturers</option>
                {manufacturers.map(mfr => (
                  <option key={mfr} value={mfr}>{mfr}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Filter className="w-4 h-4 inline mr-2" />
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.replace('_', ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Showing {filteredDevices.length} of {deviceDatabase.length} devices
            </p>
            <button
              onClick={exportDatabase}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Results
            </button>
          </div>
        </div>

        {/* Device Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDevices.map(device => {
            const enhanced = getEnhancedDeviceData(device.id);
            const discount = getDiscountPercentage(device.id);
            
            return (
            <div
              key={device.id}
              onClick={() => setSelectedDevice(device)}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200 cursor-pointer"
            >
              {/* Device Image */}
              {enhanced?.imageUrl && (
                <div className="relative h-48 bg-slate-100">
                  <img 
                    src={enhanced.imageUrl} 
                    alt={device.model}
                    className="w-full h-full object-cover"
                  />
                  {discount > 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
                      <Tag className="w-3 h-3 mr-1" />
                      {discount}% OFF
                    </div>
                  )}
                </div>
              )}
              
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{device.model}</h3>
                    <p className="text-sm text-indigo-600 font-semibold">{device.manufacturer}</p>
                  </div>
                  {device.addressable && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      Addressable
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-4">{device.description}</p>
                
                {/* Pricing */}
                {enhanced?.typicalPrice && (
                  <div className="mb-4 pb-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500">Typical Price</p>
                        <p className="text-2xl font-bold text-green-600">${enhanced.typicalPrice.toFixed(2)}</p>
                      </div>
                      {enhanced.listPrice && enhanced.listPrice > enhanced.typicalPrice && (
                        <div className="text-right">
                          <p className="text-xs text-slate-500">List Price</p>
                          <p className="text-sm text-slate-400 line-through">${enhanced.listPrice.toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Category:</span>
                  <span className="font-semibold text-slate-900 capitalize">{device.category.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Standby:</span>
                  <span className="font-semibold text-slate-900">{device.standbyCurrentMa} mA</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Alarm:</span>
                  <span className="font-semibold text-slate-900">{device.alarmCurrentMa} mA</span>
                </div>
                {device.candela && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Candela:</span>
                    <span className="font-semibold text-slate-900">{device.candela} cd</span>
                  </div>
                )}
                {device.wattage && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Wattage:</span>
                    <span className="font-semibold text-slate-900">{device.wattage} W</span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                {device.ulListed && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded">
                    UL Listed
                  </span>
                )}
                {device.nfpa72Compliant && (
                  <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded">
                    NFPA 72
                  </span>
                )}
              </div>
              </div>
            </div>
          );
          })}
        </div>

        {filteredDevices.length === 0 && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No devices found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Device Detail Modal */}
      {selectedDevice && (() => {
        const enhanced = getEnhancedDeviceData(selectedDevice.id);
        const compatiblePanels = getCompatiblePanels(selectedDevice.id);
        const discount = getDiscountPercentage(selectedDevice.id);
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Device Image Header */}
            {enhanced?.imageUrl && (
              <div className="relative h-64 bg-slate-100">
                <img 
                  src={enhanced.imageUrl} 
                  alt={selectedDevice.model}
                  className="w-full h-full object-cover"
                />
                {discount > 0 && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center">
                    <Tag className="w-4 h-4 mr-2" />
                    Save {discount}%
                  </div>
                )}
              </div>
            )}
            
            <div className="p-6 border-b border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{selectedDevice.model}</h2>
                  <p className="text-lg text-indigo-600 font-semibold">{selectedDevice.manufacturer}</p>
                </div>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Pricing Section */}
              {enhanced?.typicalPrice && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-semibold mb-1 flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        Typical Price
                      </p>
                      <p className="text-4xl font-bold text-green-600">${enhanced.typicalPrice.toFixed(2)}</p>
                    </div>
                    {enhanced.listPrice && enhanced.listPrice > enhanced.typicalPrice && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500 mb-1">List Price</p>
                        <p className="text-2xl text-slate-400 line-through">${enhanced.listPrice.toFixed(2)}</p>
                        <p className="text-sm text-green-600 font-semibold mt-1">
                          You save ${(enhanced.listPrice - enhanced.typicalPrice).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center">
                  <Info className="w-5 h-5 mr-2 text-indigo-600" />
                  Description
                </h3>
                <p className="text-slate-700">{selectedDevice.description}</p>
              </div>
              
              {/* Documentation Links */}
              {(enhanced?.dataSheetUrl || enhanced?.installationManualUrl) && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    Documentation
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {enhanced.dataSheetUrl && (
                      <a
                        href={enhanced.dataSheetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded-lg transition-colors duration-200"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Data Sheet
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </a>
                    )}
                    {enhanced.installationManualUrl && (
                      <a
                        href={enhanced.installationManualUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold rounded-lg transition-colors duration-200"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Install Manual
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Compatible Panels */}
              {compatiblePanels.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-purple-600" />
                    Compatible Panels
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {compatiblePanels.map((panel, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-2 bg-purple-50 text-purple-700 text-sm font-semibold rounded-lg border border-purple-200"
                      >
                        {panel}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Category</p>
                  <p className="text-lg font-bold text-slate-900 capitalize">{selectedDevice.category.replace('_', ' ')}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Type</p>
                  <p className="text-lg font-bold text-slate-900 capitalize">{selectedDevice.type}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Voltage</p>
                  <p className="text-lg font-bold text-slate-900">{selectedDevice.voltage.join(', ')}V</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Mounting</p>
                  <p className="text-lg font-bold text-slate-900 capitalize">{selectedDevice.mounting}</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-orange-600" />
                  Current Draw
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 mb-1">Standby Current</p>
                    <p className="text-2xl font-bold text-blue-900">{selectedDevice.standbyCurrentMa} <span className="text-lg">mA</span></p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-orange-600 mb-1">Alarm Current</p>
                    <p className="text-2xl font-bold text-orange-900">{selectedDevice.alarmCurrentMa} <span className="text-lg">mA</span></p>
                  </div>
                </div>
              </div>

              {(selectedDevice.candela || selectedDevice.wattage) && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Additional Specifications</h3>
                  <div className="space-y-2">
                    {selectedDevice.candela && (
                      <div className="flex justify-between py-2 border-b border-slate-200">
                        <span className="text-slate-600">Candela Rating</span>
                        <span className="font-semibold text-slate-900">{selectedDevice.candela} cd</span>
                      </div>
                    )}
                    {selectedDevice.wattage && (
                      <div className="flex justify-between py-2 border-b border-slate-200">
                        <span className="text-slate-600">Wattage</span>
                        <span className="font-semibold text-slate-900">{selectedDevice.wattage} W</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-slate-200">
                      <span className="text-slate-600">Addressable</span>
                      <span className="font-semibold text-slate-900">{selectedDevice.addressable ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  Certifications
                </h3>
                <div className="flex gap-3">
                  {selectedDevice.ulListed && (
                    <span className="px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg">
                      UL Listed
                    </span>
                  )}
                  {selectedDevice.nfpa72Compliant && (
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 font-semibold rounded-lg">
                      NFPA 72 Compliant
                    </span>
                  )}
                </div>
              </div>

              {selectedDevice.notes && (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Notes</h3>
                  <p className="text-slate-700 bg-slate-50 rounded-lg p-4">{selectedDevice.notes}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200">
              <button
                onClick={() => setSelectedDevice(null)}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
